// react
import {
  type Dispatch,
  type SetStateAction,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// api
import { useLazyGetDataQuery } from '@/api/recalcV2/endpoints/dataApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';
import { getDataTypeUnit } from '@/utils/dataTypeMapper';

// relative
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '../../../api/generated/recalc/endpoints/v1/units';
import type {
  AggregateType,
  ChartSeries,
  DateRange,
  ExpandedRecordedDataType,
  ReadUnitDto,
  SeriesVisibility,
  YAxis,
} from '../types';
import getDefaultVisibleDataTypes from '../utils/defaultVisibleDataTypes';

import { DateTime } from 'luxon';

const POLLING_INTERVAL_MS = 1000 * 60 * 5;

interface UseDataViewerDataArgs {
  accountId: number;
  selectedUnits: ReadUnitDto[];
  dateRange: DateRange;
  liveMode: boolean;
  aggregateOverride: AggregateType;
  isAuto: boolean;
  aggregateTypes: AggregateType[];
  initialSeriesVisibility?: SeriesVisibility;
  showAllSeriesByDefault?: boolean;
}

interface UseDataViewerDataResult {
  units: ReadUnitDto[];
  unitsLoading: boolean;
  isLoading: boolean;
  chartSeries: ChartSeries[];
  yAxes: YAxis[];
  seriesVisibility: SeriesVisibility;
  setSeriesVisibility: Dispatch<SetStateAction<SeriesVisibility>>;
  defaultVisibleDataTypes: string[];
  expandedRecordedDataTypes: ExpandedRecordedDataType[];
}

// Data types that are never aggregated — always fetched as Raw
const NON_AGGREGATED_DATA_TYPES = new Set([
  'indicative-consumption',
  'identified-motor-frequency',
  'identified-motor-frequency-score',
  'flow-consumption-normalized',
  'sample-interval',
  'quantity',
  'percent',
  'custom',
  'presence',
  'unknown',
  'single-coil-current',
  'signal-amplitude',
]);

function isNonAggregatedDataType(dataType: string): boolean {
  return NON_AGGREGATED_DATA_TYPES.has(dataType);
}

function formatAndEncodeTimestampForAPI(date: Date): string {
  const isoString = DateTime.fromJSDate(date).toISO() ?? '';
  return encodeURIComponent(isoString);
}

function buildExpandedRecordedDataTypes(
  units: ReadUnitDto[],
): ExpandedRecordedDataType[] {
  return units.flatMap((unit) =>
    (unit.RecordedDataTypes ?? []).flatMap((type) =>
      (type.DataTypes ?? []).map((datatype) => ({
        DataType: datatype,
        UnitTypeInputId: type.UnitTypeInput?.UnitTypeInputId ?? 0,
        Name: type.UnitTypeInput?.Name ?? undefined,
        UnitId: unit.UnitId,
      })),
    ),
  );
}

function getUnitSensorCustomName(
  unit: ReadUnitDto | undefined,
  unitTypeInputId: number,
): string | undefined {
  if (!unit || unitTypeInputId === 0) return undefined;

  const customName = unit.UnitSensors?.find(
    (sensor) => sensor.UnitTypeInput?.UnitTypeInputId === unitTypeInputId,
  )?.CustomName;

  const trimmedCustomName = customName?.trim();
  return trimmedCustomName ? trimmedCustomName : undefined;
}

function getTimestampMillis(timestamp: string): number {
  return Date.parse(timestamp);
}

function getSeriesId(datatype: ExpandedRecordedDataType): string {
  return `${datatype.UnitId}-${datatype.DataType}-${datatype.UnitTypeInputId}`;
}

function sortChartSeries(series: ChartSeries[]): ChartSeries[] {
  return [...series].sort((a, b) =>
    String(a.unit?.UnitId ?? '').localeCompare(
      String(b.unit?.UnitId ?? ''),
      undefined,
      {
        numeric: true,
      },
    ),
  );
}

type DataPoint = {
  timestamp: string;
  value: number;
  Base?: string;
};

function buildChartSeries({
  datatype,
  data,
  unitsById,
  t,
  liveMode,
  aggregateType,
}: {
  datatype: ExpandedRecordedDataType;
  data: DataPoint[];
  unitsById: Map<number, ReadUnitDto>;
  t: (key: string, options?: Record<string, unknown>) => string;
  liveMode: boolean;
  aggregateType: AggregateType;
}): ChartSeries | null {
  if (data.length === 0) {
    return null;
  }

  const unit = unitsById.get(datatype.UnitId);
  const translatedBaseUnit = t(`units.${datatype.DataType}`, {
    ns: 'datatypes',
    defaultValue: '',
  });
  const baseUnit =
    translatedBaseUnit ||
    data[0]?.Base ||
    getDataTypeUnit(datatype.DataType) ||
    '';
  const unitSensorCustomName = getUnitSensorCustomName(
    unit,
    datatype.UnitTypeInputId,
  );
  const seriesInputName =
    unitSensorCustomName ||
    (datatype.UnitTypeInputId === 0
      ? t('total')
      : (datatype.Name ?? t('total')));

  return {
    id: getSeriesId(datatype),
    name: `${seriesInputName}${baseUnit ? ` (${baseUnit})` : ''}`,
    type: 'line',
    data: data.map((point) => [
      getTimestampMillis(point.timestamp),
      point.value,
    ]),
    dataType: datatype.DataType,
    base: baseUnit,
    unit,
    showInNavigator: !liveMode,
    aggregateType,
  };
}

export default function useDataViewerData({
  accountId,
  selectedUnits,
  dateRange,
  liveMode,
  aggregateOverride,
  isAuto,
  aggregateTypes,
  initialSeriesVisibility,
  showAllSeriesByDefault = false,
}: UseDataViewerDataArgs): UseDataViewerDataResult {
  const { data: units, isLoading: unitsLoading } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${accountId})` },
      { skip: !accountId, pollingInterval: POLLING_INTERVAL_MS },
    );
  const [trigger] = useLazyGetDataQuery();
  const [chartSeries, setChartSeries] = useState<ChartSeries[]>([]);
  const [yAxes, setYAxes] = useState<YAxis[]>([]);
  const [seriesVisibility, setSeriesVisibility] = useState<SeriesVisibility>(
    initialSeriesVisibility ?? {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('view-data');
  const dispatch = useDispatch();
  const dataCacheRef = useRef<{
    contextKey: string;
    aggregateKey: string;
    seriesData: Map<
      string,
      { data: DataPoint[]; usedAggregate: AggregateType }
    >;
  }>({
    contextKey: '',
    aggregateKey: '',
    seriesData: new Map(),
  });

  const expandedRecordedDataTypes = useMemo(
    () => buildExpandedRecordedDataTypes(selectedUnits),
    [selectedUnits],
  );
  const unitsById = useMemo(
    () =>
      new Map<number, ReadUnitDto>(
        (units ?? []).map((unit) => [unit.UnitId, unit]),
      ),
    [units],
  );

  // Derived during render — avoids calling setState in an effect body
  const shouldClearData =
    expandedRecordedDataTypes.length === 0 || !dateRange.from || !dateRange.to;

  const availableDataTypes = useMemo(
    () =>
      Array.from(new Set(chartSeries.map((d) => d.dataType))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [chartSeries],
  );

  const defaultVisibleDataTypes = getDefaultVisibleDataTypes(
    selectedUnits,
    availableDataTypes,
  );
  const aggregateTypesKey = aggregateTypes.join('|');
  const fetchContextKey = `${dateRange.from?.toISO() ?? ''}|${dateRange.to?.toISO() ?? ''}|${aggregateOverride}|${isAuto ? 'auto' : 'manual'}|${aggregateTypesKey}`;

  useEffect(() => {
    if (shouldClearData) {
      return;
    }

    const from = formatAndEncodeTimestampForAPI(dateRange.from.toJSDate());
    const to = formatAndEncodeTimestampForAPI(dateRange.to.toJSDate());

    const toApiArgs = (unit: ExpandedRecordedDataType) => ({
      from,
      to,
      unitId: unit.UnitId,
      datatype: unit.DataType,
      unitTypeInputId: unit.UnitTypeInputId,
      aggregateType: isNonAggregatedDataType(unit.DataType)
        ? 'Raw'
        : aggregateOverride,
    });

    const toApiArgsWithAggregate = (
      unit: ExpandedRecordedDataType,
      agg: AggregateType,
    ) => ({
      from,
      to,
      unitId: unit.UnitId,
      datatype: unit.DataType,
      unitTypeInputId: unit.UnitTypeInputId,
      aggregateType: agg,
    });

    let isMounted = true;
    const previousContextKey = dataCacheRef.current.contextKey;
    const previousAggregateKey = dataCacheRef.current.aggregateKey;
    const isSameFetchContext = previousContextKey === fetchContextKey;
    const aggregateKey = `${aggregateOverride}|${isAuto ? 'auto' : 'manual'}`;
    const aggregateChanged = previousAggregateKey !== aggregateKey;

    if (!isSameFetchContext) {
      dataCacheRef.current = {
        contextKey: fetchContextKey,
        aggregateKey,
        seriesData: new Map(),
      };
    }

    const uniqueDataTypes: string[] = [];
    const axes: YAxis[] = expandedRecordedDataTypes
      .filter((datatype) => {
        if (uniqueDataTypes.includes(datatype.DataType)) return false;
        uniqueDataTypes.push(datatype.DataType);
        return true;
      })
      .map((datatype, idx) => ({
        title: { text: datatype.DataType },
        opposite: idx % 2 === 1,
      }));

    const fetchAllData = async () => {
      // Clear stale series immediately when the user explicitly changes the
      // aggregation via the toggle button group. Guard with !isAuto so that
      // zooming (which changes the auto-selected aggregate via date range) does
      // not wipe the chart during load.
      if (!isSameFetchContext && aggregateChanged && !isAuto) {
        setChartSeries([]);
        setYAxes([]);
      }

      const currentCache = dataCacheRef.current.seriesData;
      const datatypesToFetch = expandedRecordedDataTypes.filter(
        (datatype) => !currentCache.has(getSeriesId(datatype)),
      );

      if (datatypesToFetch.length > 0) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
      }

      await Promise.all(
        datatypesToFetch.map(async (datatype) => {
          let result = await trigger(toApiArgs(datatype), true);
          let usedAggregate: AggregateType = isNonAggregatedDataType(
            datatype.DataType,
          )
            ? 'Raw'
            : aggregateOverride;

          if (isAuto && !isNonAggregatedDataType(datatype.DataType)) {
            const currentIndex = aggregateTypes.indexOf(aggregateOverride);
            const sortedAggregates = aggregateTypes
              .map((agg, index) => ({
                agg,
                distance: Math.abs(index - currentIndex),
              }))
              .sort((a, b) => a.distance - b.distance)
              .map((item) => item.agg)
              .filter((agg) => agg !== aggregateOverride);

            for (const agg of sortedAggregates) {
              if ((result?.data?.length ?? 0) > 0) break;
              result = await trigger(
                toApiArgsWithAggregate(datatype, agg),
                true,
              );
              if ((result?.data?.length ?? 0) > 0) {
                usedAggregate = agg;
              }
            }
          }

          const data = (result?.data ?? []) as DataPoint[];
          if (data.length === 10000) {
            dispatch(
              showSnackbar({
                message: t('data_truncated'),
                severity: 'warning',
              }),
            );
          }

          currentCache.set(getSeriesId(datatype), { data, usedAggregate });

          if (
            aggregateOverride !== 'Hour' &&
            datatype.DataType === 'power-consumption' &&
            data.length === 0
          ) {
            dispatch(
              showSnackbar({
                message: t('no_day_aggregation'),
                severity: 'info',
              }),
            );
          }
        }),
      );

      if (!isMounted) return;

      const seriesArr = sortChartSeries(
        expandedRecordedDataTypes
          .map((datatype) => {
            const cached = currentCache.get(getSeriesId(datatype));
            return buildChartSeries({
              datatype,
              data: cached?.data ?? [],
              unitsById,
              t,
              liveMode,
              aggregateType:
                cached?.usedAggregate ??
                (isNonAggregatedDataType(datatype.DataType)
                  ? 'Raw'
                  : aggregateOverride),
            });
          })
          .filter(Boolean) as ChartSeries[],
      );

      // Mark the expensive chart render as non-urgent so React can process
      // user interactions (mouse moves, clicks) without waiting for the full
      // 30-series Highcharts update to complete first.
      startTransition(() => {
        setChartSeries(seriesArr);
        setYAxes(axes);
        setIsLoading(false);
        setSeriesVisibility((prev) => {
          const next = { ...prev };
          for (const s of seriesArr) {
            if (!(s.id in next)) {
              const isDefaultVisible = getDefaultVisibleDataTypes(
                [s.unit].filter(Boolean) as ReadUnitDto[],
              ).includes(s.dataType);

              // Keep only explicit overrides in state: default-visible series are
              // represented by missing entries, while default-hidden series are
              // represented explicitly as visible:false.
              if (!isDefaultVisible && !showAllSeriesByDefault) {
                next[s.id] = { visible: false };
              }
            }
          }
          Object.keys(next).forEach((id) => {
            if (!seriesArr.some((s) => s.id === id)) delete next[id];
          });
          return next;
        });
      });
    };

    fetchAllData();

    return () => {
      isMounted = false;
    };
  }, [
    shouldClearData,
    dateRange.from,
    dateRange.to,
    liveMode,
    trigger,
    fetchContextKey,
    expandedRecordedDataTypes,
    aggregateOverride,
    isAuto,
    aggregateTypes,
    unitsById,
    showAllSeriesByDefault,
    dispatch,
    t,
  ]);

  return {
    units: units ?? [],
    unitsLoading,
    isLoading,
    chartSeries: shouldClearData ? [] : chartSeries,
    yAxes: shouldClearData ? [] : yAxes,
    seriesVisibility,
    setSeriesVisibility,
    defaultVisibleDataTypes,
    expandedRecordedDataTypes,
  };
}
